/** What both pages need before either can draw: the measured library, the
 *  reader's two remembered preferences, and nothing else. */
import type { Library } from './engine/types'
import { setCurrency, type Currency } from './ui/format'
import { getLang, setLang, t, type Lang } from './ui/lang'

export const RATE_KEY = 'ai-cost.daily-rate-usd'
export const CURRENCY_KEY = 'ai-cost.currency'

export function need<T extends HTMLElement>(id: string): T {
  const node = document.getElementById(id)
  if (!node) throw new Error(`élément #${id} absent de la page`)
  return node as T
}

export function stored(key: string): string | null {
  try {
    return window.localStorage.getItem(key)
  } catch {
    return null // a private window throws on access; not a reason to fail
  }
}

export function remember(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value)
  } catch { /* the preference simply is not remembered */ }
}

export function storedRate(fallback: number): number {
  const raw = Number(stored(RATE_KEY))
  return Number.isFinite(raw) && raw > 0 ? raw : fallback
}

export function restoreCurrency(): void {
  setCurrency(stored(CURRENCY_KEY) === 'USD' ? 'USD' : 'EUR')
}

export function rememberCurrency(code: Currency): void {
  remember(CURRENCY_KEY, code)
}

export async function loadLibrary(): Promise<Library> {
  const response = await fetch('/data/library.json')
  if (!response.ok) throw new Error(`HTTP ${response.status}`)
  return (await response.json()) as Library
}

/** The €/$ picker, identical on both pages: every figure on either page is a
 *  conversion at a fixed rate, and the control that says so is not decoration. */
export function currencyPicker(onPick: () => void): HTMLElement {
  const picker = document.createElement('div')
  picker.className = 'currency'
  picker.setAttribute('role', 'group')
  picker.setAttribute('aria-label', t('devise d’affichage', 'display currency'))
  for (const code of ['EUR', 'USD'] as Currency[]) {
    const button = document.createElement('button')
    button.type = 'button'
    button.textContent = code === 'EUR' ? '€' : '$'
    button.className = 'currency-option'
    picker.appendChild(button)
    button.addEventListener('click', () => {
      setCurrency(code)
      rememberCurrency(code)
      onPick()
    })
  }
  return picker
}

/** Le sélecteur de langue, jumeau de celui de la devise : deux réglages d'affichage,
 *  une même pastille. Le site est écrit en français ; l'anglais est là pour qu'il serve
 *  hors de France (Armand, 21/09/2026). */
export function langPicker(onPick: () => void): HTMLElement {
  const picker = document.createElement('div')
  picker.className = 'currency'
  picker.setAttribute('role', 'group')
  picker.setAttribute('aria-label', t('langue de la page', 'page language'))
  for (const code of ['fr', 'en'] as Lang[]) {
    const button = document.createElement('button')
    button.type = 'button'
    button.textContent = code.toUpperCase()
    button.className = `currency-option${code === getLang() ? ' on' : ''}`
    button.setAttribute('aria-pressed', String(code === getLang()))
    picker.appendChild(button)
    button.addEventListener('click', () => {
      if (code === getLang()) return
      setLang(code)
      onPick()
    })
  }
  return picker
}
